import { useState } from "react";
import { Link } from "react-router-dom";
import { TicketPercent, Check, X, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Voucher redemption input.
 *
 * Props:
 *  - packageId: string  — the ALL_PACKAGES key being purchased.
 *  - originalAmount: number
 *  - onApplied: (voucher | null) => void   // { code, discount_type, discount_value, final_amount, savings }
 *  - onRequestDiscount?: () => void        // opens the discount request modal
 */
export default function VoucherInput({ packageId, originalAmount, onApplied, onRequestDiscount }) {
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showReq, setShowReq] = useState(false);

  const apply = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vouchers/validate", {
        code: code.trim().toUpperCase(),
        package_id: packageId,
      });
      setApplied(data);
      onApplied?.(data);
      toast.success(`Voucher applied — R${data.savings.toFixed(2)} off`);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Invalid voucher";
      toast.error(msg);
      setApplied(null);
      onApplied?.(null);
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setApplied(null);
    setCode("");
    onApplied?.(null);
  };

  return (
    <div className="mt-6" data-testid="voucher-input">
      {applied ? (
        <div className="flex items-center justify-between gap-3 border border-velvet-gold/50 bg-velvet-gold/5 px-4 py-3">
          <div className="flex items-center gap-3">
            <Check size={16} className="text-velvet-gold" />
            <div>
              <p className="text-xs uppercase tracking-luxe text-velvet-gold">Voucher applied</p>
              <p className="text-sm font-light text-white">
                <span className="tabular-nums">{applied.code}</span> &middot; saving R{applied.savings.toFixed(2)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={remove}
            data-testid="voucher-remove"
            className="p-2 text-white/40 transition-colors hover:text-velvet-rose"
            aria-label="Remove voucher"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <form onSubmit={apply} className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 items-center gap-2 border-b border-white/15 py-2">
            <TicketPercent size={16} className="text-velvet-gold" />
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Voucher code"
              maxLength={40}
              data-testid="voucher-code-input"
              className="w-full bg-transparent text-sm uppercase tracking-luxe text-white placeholder:text-white/30 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !code.trim()}
            data-testid="voucher-apply"
            className="rounded-full border border-white/20 px-5 py-2 text-[10px] uppercase tracking-luxe text-white transition-colors hover:border-velvet-gold hover:text-velvet-gold disabled:opacity-40"
          >
            {busy ? "Checking…" : "Apply"}
          </button>
        </form>
      )}

      <button
        type="button"
        onClick={() => (onRequestDiscount ? onRequestDiscount() : setShowReq(true))}
        data-testid="voucher-request"
        className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-luxe text-white/40 transition-colors hover:text-velvet-rose"
      >
        <HelpCircle size={12} /> Request a discount code
      </button>

      {showReq && !onRequestDiscount && (
        <DiscountRequestModal
          packageId={packageId}
          onClose={() => setShowReq(false)}
        />
      )}
    </div>
  );
}

export function DiscountRequestModal({ packageId, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", reason: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/discount-requests", { ...form, package_id: packageId });
      toast.success("Request sent — we'll reply within 24h.");
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-md"
      onClick={onClose}
      data-testid="discount-request-modal"
    >
      <div
        className="w-full max-w-md border border-velvet-gold/40 bg-velvet-coal p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Request a discount</p>
        <h3 className="mt-2 font-heading text-3xl font-light text-white">
          Ask for an invite code
        </h3>
        <p className="mt-3 text-xs font-light text-white/50">
          Tell us why you'd like a discount — our team reviews every request and
          emails your personal single-use code back.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            required
            type="text"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="discount-request-name"
            className="w-full border-b border-white/15 bg-transparent py-2 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
          />
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="discount-request-email"
            className="w-full border-b border-white/15 bg-transparent py-2 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
          />
          <textarea
            required
            rows={4}
            placeholder="Why should we invite you?"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            data-testid="discount-request-reason"
            className="w-full border-b border-white/15 bg-transparent py-2 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
          />
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="discount-request-submit"
              className="flex-1 rounded-full bg-velvet-rose py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold disabled:opacity-50"
            >
              {sending ? "Sending…" : "Submit request"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-white/15 px-5 py-3 text-xs uppercase tracking-luxe text-white/60 hover:border-white/40 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
